export enum PluralForm {
    ZERO = 'zero',
    ONE = 'one',
    TWO = 'two',
    FEW = 'few',
    MANY = 'many',
    OTHER = 'other',
}

export type TimeUnit = 'second' | 'minute' | 'hour' | 'day' | 'week' | 'month' | 'year';

export type PluralFormConfig = {
    [key in PluralForm]?: string | null;
};

export type LabelTimeConfig = {
    secondPluralForms?: PluralFormConfig;
    minutePluralForms?: PluralFormConfig;
    hourPluralForms?: PluralFormConfig;
    dayPluralForms?: PluralFormConfig;
    weekPluralForms?: PluralFormConfig;
    monthPluralForms?: PluralFormConfig;
    yearPluralForms?: PluralFormConfig;
};

export type SeparatorConfig = {
    compositeSeparator?: string;
    standaloneSeparator?: string;
};

export type PluralHandler = (count: number) => PluralForm;
export type PluralResolvers = Record<string, PluralHandler>;

export const TIME_UNITS: TimeUnit[] = ['year', 'month', 'week', 'day', 'hour', 'minute', 'second'];

export const TIME_MULTIPLIERS: Record<Uppercase<TimeUnit>, number> = {
    YEAR: 365 * 24 * 60 * 60,
    MONTH: 30 * 24 * 60 * 60, // Approximate
    WEEK: 7 * 24 * 60 * 60,
    DAY: 24 * 60 * 60,
    HOUR: 60 * 60,
    MINUTE: 60,
    SECOND: 1,
};

export const UNIT_SUFFIX = 'PluralForms';

const simpleResolver: PluralHandler = (count) => {
    if (count === 0) return PluralForm.ZERO;
    if (count === 1) return PluralForm.ONE;
    return PluralForm.OTHER;
};

const slavicResolver: PluralHandler = (count) => {
    if (count === 0) return PluralForm.ZERO;

    const mod10 = count % 10;
    const mod100 = count % 100;

    if (mod100 >= 11 && mod100 <= 14) return PluralForm.MANY;

    if (mod10 === 1) return PluralForm.ONE; // 1, 21, 31, 41, 51...
    if (mod10 >= 2 && mod10 <= 4) return PluralForm.FEW; // 2,3,4, 22,23,24, 32,33,34...

    return PluralForm.MANY; // 5,6,7,8,9,10, 15,16,17,18,19,20, 25,26,27,28,29,30...
};

const polishResolver: PluralHandler = (count) => {
    if (count === 0) return PluralForm.ZERO;
    if (count === 1) return PluralForm.ONE;

    const mod10 = count % 10;
    const mod100 = count % 100;

    // 2,3,4 but not 12,13,14
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
        return PluralForm.FEW;
    }
    return PluralForm.MANY;
};

const arabicResolver: PluralHandler = (count) => {
    if (count === 0) return PluralForm.ZERO;
    if (count === 1) return PluralForm.ONE;
    if (count === 2) return PluralForm.TWO;

    const mod100 = count % 100;

    // 3-10
    if (count >= 3 && count <= 10) return PluralForm.FEW;
    // 11-99
    if (mod100 >= 11 && mod100 <= 99) return PluralForm.MANY;

    return PluralForm.OTHER; // 100, 200, 300...
};

const noPluralsResolver: PluralHandler = (count) => {
    if (count === 0) return PluralForm.ZERO;
    return PluralForm.OTHER;
};

export const defaultPluralResolvers: PluralResolvers = {
    en: simpleResolver, // English
    de: simpleResolver, // German
    it: simpleResolver, // Italian
    id: simpleResolver, // Indonesian
    fr: simpleResolver, // French

    ru: slavicResolver, // Russian
    uk: slavicResolver, // Ukrainian

    pl: polishResolver, // Polish
    ar: arabicResolver, // Arabic
    ja: noPluralsResolver, // Japanese
    zh: noPluralsResolver, // Chinese
    ko: noPluralsResolver, // Korean
};
